import { useState, useEffect } from "react";
import {
  Link as LinkIcon, Plus, Copy, ExternalLink, Trash2, Check,
  Search, MousePointerClick, Zap, Tag, ArrowUpRight, BarChart2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";

const INITIAL_TRIGGER_LINKS = [
  {
    id: "tl-1",
    name: "VIP Discovery Booking Link",
    targetUrl: "https://deck.weddingsthyzfoto.com/book/master",
    shortUrl: "https://deck.weddingsthyzfoto.com/l/vip-book",
    slug: "vip-book",
    clicks: 142,
    uniqueClicks: 119,
    action: "Add Tag: Hot Lead + Alert Account Exec",
    status: "Active"
  },
  {
    id: "tl-2",
    name: "Commercial Case Studies & Portfolio",
    targetUrl: "https://deck.weddingsthyzfoto.com/portfolio",
    shortUrl: "https://deck.weddingsthyzfoto.com/l/portfolio",
    slug: "portfolio",
    clicks: 319,
    uniqueClicks: 264,
    action: "Add Tag: Engaged Prospect",
    status: "Active"
  },
  {
    id: "tl-3",
    name: "5-Star Review Funnel Redirect",
    targetUrl: "https://deck.weddingsthyzfoto.com/review",
    shortUrl: "https://deck.weddingsthyzfoto.com/l/leave-review",
    slug: "leave-review",
    clicks: 88,
    uniqueClicks: 82,
    action: "Remove Tag: Needs Review + Add Tag: Reviewed",
    status: "Active"
  },
  {
    id: "tl-4",
    name: "Client Onboarding Scope Portal",
    targetUrl: "https://deck.weddingsthyzfoto.com/portal",
    shortUrl: "https://deck.weddingsthyzfoto.com/l/client-portal",
    slug: "client-portal",
    clicks: 64,
    uniqueClicks: 58,
    action: "Trigger Webhook: Client Activated",
    status: "Active"
  }
];

export default function TriggerLinksTab() {
  const [links, setLinks] = useState(() => {
    const saved = localStorage.getItem("ld_trigger_links");
    if (saved) {
      try { return JSON.parse(saved); } catch {}
    }
    return INITIAL_TRIGGER_LINKS;
  });

  useEffect(() => {
    localStorage.setItem("ld_trigger_links", JSON.stringify(links));
  }, [links]);

  const [searchQuery, setSearchQuery] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);

  // Form State
  const [newLink, setNewLink] = useState({
    name: "",
    targetUrl: "https://",
    slug: "",
    action: "Add Tag: Link Clicked"
  });

  const handleCopy = (link) => {
    navigator.clipboard.writeText(link.shortUrl);
    setCopiedId(link.id);
    toast.success(`Trackable link copied: ${link.shortUrl}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = (id) => {
    setLinks(prev => prev.filter(l => l.id !== id));
    toast.success("Trigger link removed");
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!newLink.name.trim() || !newLink.targetUrl.trim()) {
      toast.error("Please fill in link name and destination URL");
      return;
    }
    const cleanSlug = (newLink.slug || newLink.name.toLowerCase().replace(/[^a-z0-9]/g, "-")).trim();
    const shortUrl = `https://deck.weddingsthyzfoto.com/l/${cleanSlug}`;

    const created = {
      id: "tl-" + Date.now(),
      name: newLink.name.trim(),
      targetUrl: newLink.targetUrl.trim(),
      shortUrl,
      slug: cleanSlug,
      clicks: 0,
      uniqueClicks: 0,
      action: newLink.action,
      status: "Active"
    };

    setLinks(prev => [created, ...prev]);
    toast.success(`Trigger link "${created.name}" created!`);
    setIsAddOpen(false);
    setNewLink({
      name: "",
      targetUrl: "https://",
      slug: "",
      action: "Add Tag: Link Clicked"
    });
  };

  const filtered = links.filter(l => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        l.name.toLowerCase().includes(q) ||
        l.targetUrl.toLowerCase().includes(q) ||
        l.shortUrl.toLowerCase().includes(q) ||
        l.action.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const totalClicks = links.reduce((acc, l) => acc + l.clicks, 0);
  const totalUnique = links.reduce((acc, l) => acc + l.uniqueClicks, 0);

  return (
    <div className="flex-1 h-full overflow-y-auto bg-slate-50 dark:bg-slate-950 p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <LinkIcon className="w-6 h-6 text-blue-600" />
              Trigger Links & Smart Redirects
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Generate trackable links that tag contacts and execute automated workflows whenever clicked in an SMS or email.
            </p>
          </div>
          <Button
            onClick={() => setIsAddOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2 shadow-xs"
          >
            <Plus className="w-4 h-4" />
            + Create Trigger Link
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Links</div>
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">{links.length}</div>
            <div className="text-xs text-blue-600 mt-1 font-medium">Tracking enabled</div>
          </Card>
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Clicks</div>
            <div className="text-2xl font-bold text-emerald-600 mt-1">{totalClicks}</div>
            <div className="text-xs text-slate-400 mt-1">Across all outbound campaigns</div>
          </Card>
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Unique Contacts</div>
            <div className="text-2xl font-bold text-purple-600 mt-1">{totalUnique}</div>
            <div className="text-xs text-slate-400 mt-1">{Math.round((totalUnique / (totalClicks || 1)) * 100)}% unique ratio</div>
          </Card>
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Automations Fired</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{totalClicks}</div>
            <div className="text-xs text-emerald-500 mt-1 font-medium">100% webhook dispatch</div>
          </Card>
        </div>

        {/* Search */}
        <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800">
          <div className="relative w-full sm:w-80">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search trigger link name or URL..."
              className="pl-8 text-xs h-8 bg-slate-50 dark:bg-slate-950"
            />
          </div>
          <span className="text-xs text-slate-400 font-medium">{filtered.length} total links</span>
        </div>

        {/* Links Table */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 text-slate-500 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="p-3.5">Trigger Link Name</th>
                  <th className="p-3.5">Trackable Shortlink</th>
                  <th className="p-3.5">Automation Action</th>
                  <th className="p-3.5 text-center">Total Clicks</th>
                  <th className="p-3.5 text-center">Unique</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((link) => (
                  <tr key={link.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="p-3.5">
                      <div className="font-semibold text-slate-900 dark:text-slate-100">{link.name}</div>
                      <div className="text-[11px] text-slate-400 truncate max-w-xs">{link.targetUrl}</div>
                    </td>
                    <td className="p-3.5 font-mono">
                      <div className="flex items-center gap-1.5 text-blue-600 bg-blue-50 dark:bg-blue-950/40 px-2 py-1 rounded w-fit">
                        <span>{link.shortUrl}</span>
                        <button
                          onClick={() => handleCopy(link)}
                          className="hover:text-blue-800 p-0.5"
                          title="Copy Link"
                        >
                          {copiedId === link.id ? (
                            <Check className="w-3 h-3 text-emerald-600" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="p-3.5">
                      <Badge variant="outline" className="text-[11px] font-normal border-slate-300 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                        ⚡ {link.action}
                      </Badge>
                    </td>
                    <td className="p-3.5 text-center font-bold text-slate-800 dark:text-slate-200">
                      {link.clicks}
                    </td>
                    <td className="p-3.5 text-center text-slate-500">
                      {link.uniqueClicks}
                    </td>
                    <td className="p-3.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          onClick={() => handleCopy(link)}
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1"
                        >
                          <Copy className="w-3 h-3" />
                          Copy
                        </Button>
                        <button
                          onClick={() => handleDelete(link.id)}
                          className="p-1.5 text-slate-400 hover:text-red-500 rounded transition-colors"
                          title="Delete trigger link"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Create Modal */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900 dark:text-slate-100">New Trigger Link</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Create a smart shortlink that triggers workflows and tags when clicked.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-3.5 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Link Name *</label>
              <Input
                required
                placeholder="e.g. VIP Consultation Calendar"
                value={newLink.name}
                onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                className="text-xs mt-1"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Target Destination URL *</label>
              <Input
                required
                placeholder="https://yourdomain.com/landing"
                value={newLink.targetUrl}
                onChange={(e) => setNewLink({ ...newLink, targetUrl: e.target.value })}
                className="text-xs mt-1"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Custom Short Slug</label>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-xs text-slate-400 font-mono">/l/</span>
                <Input
                  placeholder="custom-slug"
                  value={newLink.slug}
                  onChange={(e) => setNewLink({ ...newLink, slug: e.target.value })}
                  className="text-xs font-mono"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Trigger Automation Action</label>
              <select
                value={newLink.action}
                onChange={(e) => setNewLink({ ...newLink, action: e.target.value })}
                className="w-full mt-1 h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs dark:bg-slate-950 dark:border-slate-800"
              >
                <option value="Add Tag: Hot Lead + Alert Account Exec">Add Tag: Hot Lead + Alert Account Exec</option>
                <option value="Add Tag: Engaged Prospect">Add Tag: Engaged Prospect</option>
                <option value="Remove Tag: Needs Review + Add Tag: Reviewed">Remove Tag: Needs Review + Add Tag: Reviewed</option>
                <option value="Trigger Webhook: Client Activated">Trigger Webhook: Client Activated</option>
                <option value="Run Workflow: Speed-to-Lead Followup">Run Workflow: Speed-to-Lead Followup</option>
              </select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setIsAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                Create Trigger Link
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

